/* pamela-kiosk-mode.js
   Site-wide, but only ever active on the in-store kiosk tablet -- a regular
   customer browsing from home never sets the kiosk flag, so this script is a
   complete no-op for them.

   How it's scoped: the kiosk welcome page (/pages/kiosk-welcome) is the only
   place that sets the "cm_kiosk_mode" sessionStorage flag, and nobody reaches
   that page organically (it's only ever the Fully Kiosk Browser app's Start
   URL on the tablet). Once set, this script watches for inactivity anywhere
   on the site and returns to the welcome screen so the next customer always
   starts fresh instead of inheriting someone else's in-progress design.
*/
(function () {
  "use strict";

  const KIOSK_FLAG_KEY = "cm_kiosk_mode";
  const WELCOME_PATH = "/pages/kiosk-welcome";
  const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes of no touch/click/key/scroll

  // If a customer steps away mid-design (bathroom break, thinking it over
  // outside) and comes back within this window, the welcome screen offers a
  // "Continue Your Design" option straight back into their exact in-progress
  // design -- no need to track them down via the leads page. The site already
  // reflects the draft's resume token in the page URL during active editing
  // (see reflectTokenInUrl in pamela-drafts.js -- built so a refresh doesn't
  // lose the design), so that URL IS the resume link; this just remembers it
  // across the idle-redirect for a little while.
  //
  // The storage key ("cm_kiosk_pending_resume") is intentionally hardcoded
  // again in page.kiosk-welcome.json's own inline script rather than read
  // from a shared constant here -- this script loads deferred, but the
  // welcome page's inline script runs immediately during parsing, before a
  // deferred script has necessarily executed. Same reasoning as why the
  // kiosk-mode flag itself is set directly by the welcome page instead of
  // through this file.
  const RESUME_STORAGE_KEY = "cm_kiosk_pending_resume";

  const isKioskMode = () => {
    try {
      return sessionStorage.getItem(KIOSK_FLAG_KEY) === "1";
    } catch (_) {
      return false;
    }
  };

  // The welcome page itself sets this flag directly on load (see
  // page.kiosk-welcome.json) -- not routed through this deferred script, so
  // there's no load-order dependency between the two.
  if (!isKioskMode()) return;

  // Already on the welcome screen -- nothing to idle back to.
  if (window.location.pathname.indexOf(WELCOME_PATH) === 0) return;

  let idleTimer = null;

  function saveResumeUrlIfActiveDraft() {
    try {
      const url = new URL(window.location.href);
      const token = url.searchParams.get("token") || window.cmDraftToken || "";
      if (!token) return; // just browsing, nothing in progress to offer back
      localStorage.setItem(
        RESUME_STORAGE_KEY,
        JSON.stringify({ url: window.location.href, savedAt: Date.now() })
      );
    } catch (_) {}
  }

  // Cart-clear and lead-gate-pass-clear (the "next customer" cleanup) live
  // in page.kiosk-welcome.json's own script instead of here -- that way they
  // run no matter HOW someone reaches the welcome page: this 5-minute idle
  // timeout, OR Pamela manually jumping there via Fully Kiosk's own "Goto
  // Start URL" admin action for back-to-back customers without waiting for
  // the timer. This script's only remaining job is saving the resume link,
  // which has to happen here (needs the departing page's own URL/token --
  // info the welcome page can't see once it's already navigated away).
  function goToWelcome() {
    saveResumeUrlIfActiveDraft();
    window.location.href = WELCOME_PATH;
  }

  // Exposed so the site-wide "Start Over" button (added in theme.liquid,
  // only visible in kiosk mode) can trigger the exact same safe path back to
  // the welcome screen -- same resume-link save first, same cleanup once the
  // welcome page loads -- instead of the 7-tap admin gesture.
  window.PAMELA_KIOSK_GO_HOME = goToWelcome;

  function resetIdleTimer() {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(goToWelcome, IDLE_TIMEOUT_MS);
  }

  ["touchstart", "mousedown", "keydown", "scroll", "pointerdown"].forEach((evt) => {
    window.addEventListener(evt, resetIdleTimer, { passive: true });
  });

  resetIdleTimer();

  // The resume link used to only get saved at the moment this 5-minute idle
  // timeout fired -- fine as long as this timeout was the fastest thing that
  // could ever pull someone off the design page. It no longer is: the
  // tablet's own screen-sleep is configured far more aggressively (as low as
  // 20s) and separately from this JS timer, and waking it can land back on
  // the welcome screen well before 5 minutes of "idle" ever accumulates here
  // -- confirmed 2026-07-29, customers were losing in-progress designs to
  // this gap ("Continue Your Design" never showing up after the tablet slept
  // and was woken with the power button). Keep the resume link continuously
  // fresh instead of only saving it at the one moment this timer expects to
  // be the thing that interrupts the customer.
  setInterval(saveResumeUrlIfActiveDraft, 10 * 1000);
})();
